import { createSlice, PayloadAction, isAnyOf } from '@reduxjs/toolkit'

import { ChannelType } from 'common/core/constants/ChannelType'

import { DefaultReplyChannels, getInitialDefaultReplyChannelState } from '../defaultReplyConstants'
import {
  DefaultReplyState,
  DefaultReplyChannelState,
  DefaultReplySources,
} from '../defaultReplyInterfaces'

import {
  changeDefaultReplyType,
  changeInstagramDefaultReplySources,
  loadDefaultReply,
  removeDefaultReplyFlow,
  setDefaultReplyFlow,
  updateDefaultReplyByChannel,
  updateTriggerDefaultReplyEnabled,
} from './actions'

const getInitialState = (): DefaultReplyState => {
  const state: DefaultReplyState = DefaultReplyChannels.reduce((acc, channel) => {
    acc[channel] = getInitialDefaultReplyChannelState()

    return acc
  }, {} as DefaultReplyState)

  return state
}

const dataToChannelState = (data: SafeUnknownObject): DefaultReplyChannelState => ({
  flowId: ((data?.flow as SafeUnknownObject)?.ns as string) || null,
  name: ((data?.flow as SafeUnknownObject)?.name as string) || '',
  chId: (data?.content_holder as string) || null,
  sendType: (data?.type as string) || '',
  sources: (data?.sources as DefaultReplySources) || null,
  enabled: (data?.enabled as boolean) ?? null,
  fetching: false,
  isFetched: true,
})

const getChannelFromAction = (
  action: PayloadAction<unknown> & { meta?: { arg?: unknown } },
  deafualtValue: ChannelType = ChannelType.FB,
): ChannelType => {
  return (
    (action.meta?.arg as { channel?: ChannelType })?.channel ||
    (action.payload as { channel?: ChannelType })?.channel ||
    deafualtValue
  )
}

const defaultReplySlice = createSlice({
  name: 'defaultReply',
  initialState: getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadDefaultReply.pending, (state, action) => {
        const channel = getChannelFromAction(action)
        state[channel].fetching = true
      })
      .addCase(loadDefaultReply.rejected, (state, action) => {
        const channel = getChannelFromAction(action)
        state[channel].fetching = false
      })
      .addCase(removeDefaultReplyFlow.fulfilled, (state, action) => {
        const channel = getChannelFromAction(action)
        state[channel].flowId = null
        state[channel].name = ''
      })
      .addCase(changeInstagramDefaultReplySources.fulfilled, (state, action) => {
        const channel = getChannelFromAction(action, ChannelType.INSTAGRAM)
        state[channel].sources = action.payload.sources
      })

      .addCase(updateTriggerDefaultReplyEnabled.rejected, (state, action) => {
        const channel = getChannelFromAction(action)
        state[channel].enabled = false
        state[channel].isFetched = true
      })
      .addCase(updateDefaultReplyByChannel, (state, action) => {
        const channel = action.payload.channel
        state[channel] = {
          ...state[channel],
          ...dataToChannelState(action.payload.data),
        }
      })
      .addMatcher(
        isAnyOf(
          loadDefaultReply.fulfilled,
          updateTriggerDefaultReplyEnabled.fulfilled,
          setDefaultReplyFlow.fulfilled,
          changeDefaultReplyType.fulfilled,
        ),
        (state, action) => {
          const channel = getChannelFromAction(action)
          state[channel] = {
            ...state[channel],
            ...dataToChannelState(action.payload),
          }
        },
      )
  },
})

export const defaultReplyReducer = defaultReplySlice.reducer
